import {
    CreateEventCommand,
    EventPeriodEnum,
    EventTypeEnum,
    EventVm,
    ExclusionTypeEnum,
    MatchDetailVm,
    ShotTypeEnum,
} from '../utils/api';
import { OutboxRecord, tempEventIdFromKey } from './offlineEventStore';

/** Extra fields we hang on EventVm instances for pending/outbox UI. */
export type PendingEventFields = {
    clientEventKey?: string;
    isPending?: boolean;
};

export type LocalEventVm = EventVm & PendingEventFields;

export const isPendingEvent = (event: EventVm | LocalEventVm): boolean =>
    !!(event as LocalEventVm).isPending || !!(event as LocalEventVm).clientEventKey;

export const getClientEventKey = (event: EventVm | LocalEventVm): string | undefined =>
    (event as LocalEventVm).clientEventKey;

export const markPending = (event: EventVm, clientEventKey: string): LocalEventVm => {
    const local = event as LocalEventVm;
    local.clientEventKey = clientEventKey;
    local.isPending = true;
    return local;
};

export const markPublished = (event: EventVm): LocalEventVm => {
    const local = event as LocalEventVm;
    delete local.clientEventKey;
    delete local.isPending;
    return local;
};

/**
 * Build a displayable EventVm from a create command so the log can show the
 * event instantly (red border) before the server assigns an id.
 */
export const optimisticEventFromCommand = (
    command: CreateEventCommand,
    clientEventKey: string
): LocalEventVm => {
    const event = new EventVm({
        id: tempEventIdFromKey(clientEventKey),
        matchId: command.matchId,
        typeId: command.typeId,
        periodId: command.periodId,
        minute: command.minute,
        seconds: command.seconds,
        activeHomeGoalkeeperMatchPlayerId: command.activeHomeGoalkeeperMatchPlayerId,
        activeAwayGoalkeeperMatchPlayerId: command.activeAwayGoalkeeperMatchPlayerId,
        refereeMistake: command.refereeMistake,
        period: EventPeriodEnum[command.periodId] || '',
        type: EventTypeEnum[command.typeId] || '',
    } as any);

    if (command.typeId === EventTypeEnum.Shot && command.shot) {
        event.shot = {
            ...command.shot,
            type: ShotTypeEnum[command.shot.typeId] || '',
            team: undefined,
            takenBy: undefined,
        } as any;
    }
    if (command.typeId === EventTypeEnum.Exclusion && command.exclusion) {
        event.exclusion = {
            ...command.exclusion,
            type: ExclusionTypeEnum[command.exclusion.typeId] || '',
            team: undefined,
            excludedPlayer: undefined,
            fouledPlayer: undefined,
        } as any;
    }
    if (command.typeId === EventTypeEnum.Timeout && command.timeout) {
        event.timeout = { ...command.timeout, team: undefined } as any;
    }
    if (command.typeId === EventTypeEnum.Card && command.card) {
        event.card = { ...command.card } as any;
    }
    if (command.typeId === EventTypeEnum.Turnover && command.turnover) {
        event.turnover = { ...command.turnover } as any;
    }
    if (command.typeId === EventTypeEnum.Swim_Off && command.swimoff) {
        event.swimoff = { ...command.swimoff } as any;
    }
    if (command.typeId === EventTypeEnum.Challenge && command.challenge) {
        event.challenge = { ...command.challenge } as any;
    }
    if (command.typeId === EventTypeEnum.Goalkeeper_Change && command.goalkeeperChange) {
        event.goalkeeperChange = { ...command.goalkeeperChange } as any;
    }

    return markPending(event, clientEventKey);
};

export const restoreOptimisticFromOutbox = (raw: unknown, clientEventKey: string): LocalEventVm => {
    const event = EventVm.fromJS(raw || {});
    if (!event.id || event.id > 0) {
        event.id = tempEventIdFromKey(clientEventKey);
    }
    return markPending(event, clientEventKey);
};

/** True if local row is a stand-in for this server event (pending and/or temp id). */
export const isLocalTwinOf = (local: EventVm, serverEvent: EventVm): boolean => {
    if (serverEvent.id > 0 && local.id === serverEvent.id) return true;
    if (!eventsLookSame(local, serverEvent)) return false;
    return isPendingEvent(local) || local.id < 0;
};

/** Replace pending row(s) with the server event; never leave a duplicate id. */
export const replacePendingWithServer = (
    events: EventVm[],
    clientEventKey: string,
    serverEvent: EventVm
): EventVm[] => {
    // Clone so we never clear pending flags on an object still held in state.
    const published = markPublished(new EventVm(serverEvent as any));
    const filtered = events.filter((e) => {
        if (clientEventKey && getClientEventKey(e) === clientEventKey) return false;
        if (isLocalTwinOf(e, serverEvent)) return false;
        return true;
    });
    return dedupeEventsByServerId([...filtered, published]);
};

/** Upsert a SignalR/server event and drop any optimistic twin. */
export const upsertServerEvent = (events: EventVm[], serverEvent: EventVm): EventVm[] => {
    const published = markPublished(new EventVm(serverEvent as any));
    const filtered = events.filter((e) => !isLocalTwinOf(e, serverEvent));
    return dedupeEventsByServerId([...filtered, published]);
};

export const removePendingByKey = (events: EventVm[], clientEventKey: string): EventVm[] =>
    events.filter((e) => getClientEventKey(e) !== clientEventKey);

/** Rough match so a SignalR echo / server refetch can drop the local optimistic row. */
export const eventsLookSame = (a: EventVm, b: EventVm): boolean => {
    if (a.periodId !== b.periodId || a.minute !== b.minute || a.seconds !== b.seconds) {
        return false;
    }
    if (a.typeId !== b.typeId) return false;
    if (a.typeId === EventTypeEnum.Shot) {
        return (
            a.shot?.teamId === b.shot?.teamId &&
            a.shot?.takenById === b.shot?.takenById &&
            !!a.shot?.isGoal === !!b.shot?.isGoal
        );
    }
    if (a.typeId === EventTypeEnum.Exclusion) {
        return (
            a.exclusion?.teamId === b.exclusion?.teamId &&
            a.exclusion?.excludedPlayerId === b.exclusion?.excludedPlayerId &&
            a.exclusion?.typeId === b.exclusion?.typeId
        );
    }
    if (a.typeId === EventTypeEnum.Timeout) {
        return a.timeout?.teamId === b.timeout?.teamId;
    }
    return true;
};

/**
 * Rehydrate outbox rows into the event log after a load/refresh:
 * creates come back as optimistic rows (skipping server twins), pending
 * update/delete re-mark their server row; goal creates bump the score.
 */
export const applyOutboxRowsToState = (
    rows: OutboxRecord[],
    prevEvents: EventVm[],
    prevMatch: MatchDetailVm
): { events: EventVm[]; match: MatchDetailVm } => {
    let events = [...prevEvents];
    let match = prevMatch;
    for (const row of rows) {
        if (row.op === 'create') {
            if (events.some((e) => getClientEventKey(e) === row.clientEventKey)) continue;
            const opt = restoreOptimisticFromOutbox(row.optimisticEvent, row.clientEventKey);
            // Flush/SignalR may already have the published row while outbox
            // list was still stale - don't re-add the optimistic twin.
            if (events.some((e) => e.id > 0 && eventsLookSame(e, opt))) continue;
            events.push(opt);
            if (opt.typeId === EventTypeEnum.Shot && opt.shot?.isGoal) {
                match = adjustMatchGoalsForShot(match, opt.shot.teamId, opt.periodId, 1);
            }
        } else if ((row.op === 'update' || row.op === 'delete') && row.serverEventId != null) {
            events = events.map((e) =>
                e.id === row.serverEventId ? markPending(e, row.clientEventKey) : e
            );
        }
    }
    return { events, match };
};

/**
 * Server refetch result + local list → server truth plus still-pending local
 * rows (skip locals the server already has, by fingerprint or client key).
 */
export const mergeServerWithLocalPending = (
    serverEvents: EventVm[],
    localEvents: EventVm[]
): EventVm[] => {
    const merged = [...serverEvents];
    for (const local of localEvents) {
        if (!isPendingEvent(local) && local.id >= 0) continue;
        if (merged.some((s) => s.id > 0 && eventsLookSame(s, local))) continue;
        if (merged.some((s) => getClientEventKey(s) === getClientEventKey(local))) continue;
        merged.push(local);
    }
    return merged;
};

/**
 * Server match totals miss goals still queued locally (negative-id creates);
 * bump them back so the scoreboard survives a refetch.
 */
export const bumpMatchForPendingGoals = (
    match: MatchDetailVm,
    events: EventVm[]
): MatchDetailVm => {
    let next = match;
    for (const e of events) {
        if (e.id < 0 && e.typeId === EventTypeEnum.Shot && e.shot?.isGoal) {
            next = adjustMatchGoalsForShot(next, e.shot.teamId, e.periodId, 1);
        }
    }
    return next;
};

/** After a remote upsert, ensure at most one row per positive server id. */
export const dedupeEventsByServerId = (events: EventVm[]): EventVm[] => {
    const seen = new Set<number>();
    const out: EventVm[] = [];
    // Walk newest-last so earlier pending stays until we filter; prefer published.
    for (let i = events.length - 1; i >= 0; i -= 1) {
        const e = events[i];
        if (e.id > 0) {
            if (seen.has(e.id)) continue;
            seen.add(e.id);
        }
        out.push(e);
    }
    out.reverse();
    // Drop pending / temp-id twins of a published row we kept.
    return out.filter((e) => {
        if (e.id > 0 && !isPendingEvent(e)) return true;
        return !out.some((p) => !isPendingEvent(p) && p.id > 0 && eventsLookSame(e, p));
    });
};

export const adjustMatchGoalsForShot = (
    match: MatchDetailVm,
    teamId: number | undefined,
    periodId: EventPeriodEnum,
    delta: 1 | -1
): MatchDetailVm => {
    if (teamId == null) return match;
    const isHome = teamId === match.homeTeamId;
    const next = { ...match } as MatchDetailVm;
    const add = (key: keyof MatchDetailVm): void => {
        const cur = (next[key] as number) || 0;
        (next as any)[key] = Math.max(0, cur + delta);
    };
    if (isHome) {
        add('homeTeamGoalsTotal');
        switch (periodId) {
            case EventPeriodEnum.First_Period:
                add('homeTeamGoalsQ1');
                break;
            case EventPeriodEnum.Second_Period:
                add('homeTeamGoalsQ2');
                break;
            case EventPeriodEnum.Third_Period:
                add('homeTeamGoalsQ3');
                break;
            case EventPeriodEnum.Fourth_Period:
                add('homeTeamGoalsQ4');
                break;
            case EventPeriodEnum.Overtime:
                add('homeTeamGoalsOT');
                break;
            default:
                break;
        }
    } else if (teamId === match.awayTeamId) {
        add('awayTeamGoalsTotal');
        switch (periodId) {
            case EventPeriodEnum.First_Period:
                add('awayTeamGoalsQ1');
                break;
            case EventPeriodEnum.Second_Period:
                add('awayTeamGoalsQ2');
                break;
            case EventPeriodEnum.Third_Period:
                add('awayTeamGoalsQ3');
                break;
            case EventPeriodEnum.Fourth_Period:
                add('awayTeamGoalsQ4');
                break;
            case EventPeriodEnum.Overtime:
                add('awayTeamGoalsOT');
                break;
            default:
                break;
        }
    }
    return next;
};
